import { z } from 'zod';
import { wayneFetch } from './wayneFetch';

export const SubtypeSchema = z.enum(['FULL_AUTO', 'MULTI_AGENT_WORKFLOW', 'PIPELINE_BUILD', 'SCHEDULED_JOB']);
export type Subtype = z.infer<typeof SubtypeSchema>;

export const StatusSchema = z.enum(['QUEUED', 'ACTIVE', 'PAUSED', 'COMPLETED', 'FAILED', 'ABORTED']);
export type Status = z.infer<typeof StatusSchema>;

export const AgentIdSchema = z.enum([
  'developer', 'manager', 'project-manager', 'qa-testing', 'devops',
  'security', 'data-engineer', 'support-engineer', 'analyst',
]);
export type AgentId = z.infer<typeof AgentIdSchema>;

export const PrioritySchema = z.enum(['high', 'medium', 'low']);
export const OutcomeSchema = z.enum(['success', 'warning', 'failure']);

export const TaskRecordSchema = z.object({
  pk: z.string(),
  sk: z.string(),
  runId: z.string(),
  employeeId: z.string(),
  subtype: SubtypeSchema,
  status: StatusSchema,
  agent: AgentIdSchema,
  title: z.string(),
  description: z.string(),
  priority: PrioritySchema,
  progressPct: z.number(),
  currentStep: z.string(),
  createdAt: z.number(),
  updatedAt: z.number(),
  startedAt: z.number().optional(),
  completedAt: z.number().optional(),
  outcome: OutcomeSchema.optional(),
  subtypeData: z.record(z.string(), z.unknown()).default({}),
  correlation: z.record(z.string(), z.unknown()).default({}),
});
export type TaskRecord = z.infer<typeof TaskRecordSchema>;

export const TaskEventSchema = z.object({
  pk: z.string(),
  sk: z.string(),
  ts: z.number(),
  type: z.enum(['phase', 'tool', 'token', 'message', 'error', 'build.line', 'cr.update']),
  payload: z.record(z.string(), z.unknown()).default({}),
});
export type TaskEvent = z.infer<typeof TaskEventSchema>;

export interface TaskListPage {
  readonly items: readonly TaskRecord[];
  readonly nextCursor: string | null;
}
export interface TaskEventPage {
  readonly items: readonly TaskEvent[];
  readonly nextCursor: string | null;
}

export interface ListTasksFilter {
  readonly status?: Status;
  readonly subtype?: Subtype;
  readonly limit?: number;
  readonly cursor?: string;
}

function qs(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&');
}

export async function listTasks(filter: ListTasksFilter = {}): Promise<TaskListPage> {
  const raw = await wayneFetch<unknown>(`/tasks${qs({ ...filter })}`, { method: 'GET' });
  const parsed = z.object({
    items: z.array(TaskRecordSchema),
    nextCursor: z.string().nullable(),
  }).parse(raw);
  return parsed;
}

export async function getTask(runId: string): Promise<TaskRecord> {
  const raw = await wayneFetch<unknown>(`/tasks/${encodeURIComponent(runId)}`, { method: 'GET' });
  return TaskRecordSchema.parse(raw);
}

export interface CreateTaskBody {
  readonly subtype: Subtype;
  readonly title: string;
  readonly description: string;
  readonly agent?: AgentId;
  readonly priority?: 'high' | 'medium' | 'low';
  readonly subtypeData?: Record<string, unknown>;
  readonly correlation?: Record<string, unknown>;
}
export async function createTask(body: CreateTaskBody): Promise<TaskRecord> {
  const raw = await wayneFetch<unknown>('/tasks', { method: 'POST', body });
  return TaskRecordSchema.parse(raw);
}

export type PatchTaskBody = { readonly status: Status };
export async function patchTask(runId: string, body: PatchTaskBody): Promise<TaskRecord> {
  const raw = await wayneFetch<unknown>(`/tasks/${encodeURIComponent(runId)}`, { method: 'PATCH', body });
  return TaskRecordSchema.parse(raw);
}

export async function listTaskEvents(runId: string, opts: { limit?: number; cursor?: string; fromSeq?: number } = {}): Promise<TaskEventPage> {
  const raw = await wayneFetch<unknown>(`/tasks/${encodeURIComponent(runId)}/events${qs({ ...opts })}`, { method: 'GET' });
  return z.object({
    items: z.array(TaskEventSchema),
    nextCursor: z.string().nullable(),
  }).parse(raw);
}

// ---------------------------------------------------------------------------
// FULL_AUTO chat-side helpers (Phase 3)
// ---------------------------------------------------------------------------

export interface ChatFullAutoSubmit {
  readonly sessionId: string;
  readonly text: string;
  /** Origin of the FULL_AUTO turn — defaults to 'slash' for the
   *  slash-command path. Set to 'confirm' when the user clicks Yes on a
   *  natural-language fullauto.confirm card so the backend records the
   *  decision provenance. */
  readonly source?: 'slash' | 'confirm';
}

/** Submit a FULL_AUTO turn to the chat backend.
 *
 * The body shape (`{source, confirmedFullAuto: true}`) is what the
 * chat_processor's `classify_intent` uses to bypass the LLM classifier
 * and (for slash) the natural-language confirmation card. This goes
 * through the chat endpoint (not `/tasks` directly) so the full
 * pipeline — ack card → live card → terminal card — streams back over
 * the same WebSocket session.
 */
export async function submitChatFullAuto(req: ChatFullAutoSubmit): Promise<void> {
  const resp = await fetch('/api/chat', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      sessionId: req.sessionId,
      text: req.text,
      source: req.source ?? 'slash',
      confirmedFullAuto: true,
    }),
  });
  if (!resp.ok) {
    const detail = await resp.text().catch(() => '');
    throw new Error(`submitChatFullAuto failed: ${resp.status} ${detail}`);
  }
}

// ---------------------------------------------------------------------------
// PATCH helper (Phase 3 - Task 16)
// ---------------------------------------------------------------------------

export type TaskStatusPatch = 'PAUSED' | 'ABORTED' | 'ACTIVE';

/**
 * PATCH /api/tasks/:runId — used by Pause / Abort / Resume buttons in both
 * the Mission Control drawer and the inline ChatTaskLiveCard.
 *
 * Goes through the raw `fetch` (not `wayneFetch`) so the chat-side card and
 * MC drawer can share a thin client without bringing in the Zod-validated
 * `patchTask` round-trip — the response body isn't needed for these actions.
 */
export async function patchTaskStatus(req: {
  readonly runId: string;
  readonly status: TaskStatusPatch;
}): Promise<void> {
  const resp = await fetch(`/api/tasks/${encodeURIComponent(req.runId)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ status: req.status }),
  });
  if (!resp.ok) {
    const detail = await resp.text().catch(() => '');
    throw new Error(`patchTaskStatus failed: ${resp.status} ${detail}`);
  }
}

// ---------------------------------------------------------------------------
// Publish CR (Phase 4 - Task 30)
// ---------------------------------------------------------------------------

export interface PublishCrRequest {
  readonly runId: string;
}

export interface PublishCrResponse {
  readonly crId: string;
  readonly state: 'publishing' | 'published';
}

/**
 * POST /api/tasks/:runId/publish-cr — flips a draft CR to published via the
 * task_orchestrator handler (Phase 4 backend Tasks 23-25). The handler mints
 * a fresh OBO, calls CriticService.PublishRevision, and stamps DDB.
 */
export async function publishCr(req: PublishCrRequest): Promise<PublishCrResponse> {
  const resp = await fetch(`/api/tasks/${encodeURIComponent(req.runId)}/publish-cr`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: '',
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`publishCr failed: ${resp.status} ${text}`);
  }
  return (await resp.json()) as PublishCrResponse;
}

// ---------------------------------------------------------------------------
// Control PATCH (Phase 4 - Task 34) — pause / abort / resume the dispatcher
// ---------------------------------------------------------------------------

export type ControlSignal = 'PAUSE' | 'ABORT' | 'RESUME';

export interface PatchControlSignalRequest {
  readonly runId: string;
  readonly signal: ControlSignal;
}

/**
 * PATCH /api/tasks/:runId/control — writes `controlSignal` onto the
 * wayne-tasks row. The dispatcher polls every 10s (Phase 4 Task 21) and
 * routes to runner.pause_abort.{pause_run, abort_run, resume_run}.
 *
 * Distinct from {@link patchTaskStatus}: that endpoint mutates the
 * task `status` field directly (legacy Phase 3 surface used for manual
 * Mission Control state corrections). Phase 4 introduces the control
 * signal as the canonical way to request a state change because the
 * Fargate dispatcher owns the actual transition (e.g. pause must
 * snapshot the workspace before flipping status to PAUSED).
 */
export async function patchControlSignal(req: PatchControlSignalRequest): Promise<void> {
  const resp = await fetch(`/api/tasks/${encodeURIComponent(req.runId)}/control`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ signal: req.signal }),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`patchControlSignal ${req.signal} failed: ${resp.status} ${text}`);
  }
}
